import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import useFetch from "../../../hooks/useFetch";
import { useAuth } from "../../../hooks/authContext";
import { API } from "../../../common/Constants";
import { getAPIHeaders, 
    setAccesss, 
    setAuthToken, 
    setRefreshAuthToken, 
    setTenantID , 
    getTenant ,
    setRememberMe as storeRememebrMe, 
    getRememberMe
} from "../../../common/Utils";
import PreLoader from "../../../core/common/preLoader";
import { useAccess } from "../../../hooks/accessContext";
import { DEFAULT_TENANT } from "../../../environment";
import HeaderLogo from "../../../core/common/headerLogo";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import analyticsService from "../../../services/analyticsService";
import store from "../../../core/data/redux/store";


type PasswordField = "password";

const Login = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const [inProgress, setProgress] = useState<boolean>(false);
  const {setAuthUser, setIsLoggedIn} = useAuth();
  const {setUserAccess} = useAccess()
  const [searchParams] = useSearchParams();
  const {isLoading, error, data, status, callFetch } = useFetch();

  const [username, setUserName] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [tenant, setTenant] = useState<string | undefined>(DEFAULT_TENANT || getTenant() || undefined);
  const [rememberMe, setRememberMe]  = useState<boolean>(false || getRememberMe());
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
      setProgress(isLoading)
  }, [isLoading]);

  useEffect(() => {
      if(data && status && status < 299){
          setAuthUser(username);
          setIsLoggedIn(true);
          reset();
          onSuccess(data);
      }
  }, [status]);

  useEffect(() => {
    if(error){
      if(error.response?.data){
        setErrorMessage(error.response?.data)
        
        // Track login failure
        analyticsService.trackEvent(
          'User',
          'Login Failed',
          `Error: ${error.response?.data}`,
          0
        );
      } else{
        setErrorMessage('Something went wrong please try again later.')
        
        // Track login failure
        analyticsService.trackEvent(
          'User',
          'Login Failed',
          'Unknown Error',
          0
        );
      }
    }
  }, [error]);

  const reset = () => {
      setUserName(undefined)
      setPassword(undefined)
  }

  const handleSubmit = (event:any) => {
    event.preventDefault()
    if(!tenant){
      setErrorMessage("Please provide tenant")
    } else if(!username){
      setErrorMessage("Please provide username")
    } else if(!password){
      setErrorMessage("Please provide password")
    } else {
      setErrorMessage("")
      callFetch(API.LOGIN,
          'POST',
          {'X-Tenant-ID': tenant},
          null,
          {
              username: username,
              password: password
          }
      ).catch((err) => {});
    }
  }

  const fetchAccess = () => {
      callFetch(API.USER_ACCESS,
        'GET',
      getAPIHeaders(),
        null,
        null
      ).then((res)=>{
        if(res && status && status < 299){
          setAccesss(res)
          setUserAccess(res)
        } 
      }).catch((err) => {});
  }
  const onSuccess = (data:any) => {
    setAuthToken(data?.token)  
    if(rememberMe){
      storeRememebrMe(true);
      setRefreshAuthToken(data?.refreshToken)
    } else{
      storeRememebrMe(false);
    }
    setTenantID(tenant)
    fetchAccess()
    
    // Track successful login
    analyticsService.trackEvent(
      'User',
      'Login',
      `Admin Login: ${username}`,
      1
    );
    
    navigationPath()
  };

  const navigationPath = () => {
      navigation(searchParams.get('path') || routes.adminDashboard);
  };

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  return (
    <div className="container-fuild"> 
      <PreLoader showLoader={inProgress} />
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-5">
            <div className="login-background position-relative d-lg-flex align-items-center justify-content-center d-none flex-wrap vh-100">
              <div className="authentication-card w-100">
                <div className="authen-overlay-item border w-100">
                  <h1 className="text-white display-1">
                    Centralized control <br />  for seamless school <br /> management.
                  </h1>
                  <div className="my-4 mx-auto authen-overlay-img">
                    <ImageWithBasePath src="assets/img/bg/authentication-bg-02.svg" alt="Img" />
                  </div>
                  <div>
                    <p className="text-white fs-20 fw-semibold text-center">
                      Efficiently manage your workforce, streamline <br />{" "}
                      operations effortlessly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap">
              <div className="col-md-7 mx-auto vh-100">
                <form className="vh-100">
                  <div className="vh-100 d-flex flex-column justify-content-between p-4 pb-0">
                    <div className=" mx-auto mb-5 text-center">
                      <HeaderLogo />
                    </div>
                    <div className="">
                      <div className="text-center mb-3">
                        <h2 className="mb-2">Sign In</h2>
                        <p className="mb-0">Please enter your details to sign in</p>
                      </div>
                      <div className="mb-3" hidden={DEFAULT_TENANT ? true : false}>
                        <label className="form-label">Tenant ID</label>
                        <div className="input-group">
                          <input
                            type="text"
                            defaultValue=""
                            placeholder="Tenant ID"
                            className="form-control border-end-0"
                            onChange={(e)=>setTenant(e.target.value)} value={tenant}
                          />
                          <span className="input-group-text border-start-0">
                            <i className="ti ti-building" />
                          </span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Username</label>
                        <div className="input-group">
                          <input
                            type="text"
                            defaultValue=""
                            placeholder="Enter your username"
                            className="form-control border-end-0"
                            onChange={(e)=>setUserName(e.target.value)} value={username}
                          />
                          <span className="input-group-text border-start-0">
                            <i className="ti ti-user" />
                          </span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Password</label>
                        <div className="pass-group">
                        <input
                            type={
                              passwordVisibility.password
                                ? "text"
                                : "password"
                            }
                            placeholder="Enter your password"
                            className="pass-input form-control"
                            onChange={(e)=>setPassword(e.target.value)} value={password}
                          />
                          <span
                            className={`ti toggle-passwords ${passwordVisibility.password
                              ? "ti-eye"
                              : "ti-eye-off"
                              }`}
                            onClick={() =>
                              togglePasswordVisibility("password")
                            }
                          ></span>
                        </div>
                      </div>
                      <div className="mb-3">
                        {
                         <div className="text-center" style={{height:12}}>
                          {
                            errorMessage
                            ?
                              <>
                                <span className="link-danger">
                                <i className="ti ti-alert-triangle" /> {errorMessage}
                                </span>
                              </>
                            :
                              <></>
                            }
                          </div>
                         
                        }
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                          <div className="form-check form-check-md mb-0">
                            <input
                              className="form-check-input"
                              id="remember_me"
                              type="checkbox"
                              onChange={(e:any) => {
                                setRememberMe(e.target.checked)
                              }}
                              defaultChecked={rememberMe}
                            />
                            <label
                              htmlFor="remember_me"
                              className="form-check-label mt-0"
                            >
                              Remember Me
                            </label>
                          </div>
                        </div>
                        <div className="text-end">
                          <Link to={all_routes.forgotPassword} className="hover-a">
                            Forgot Password?
                          </Link>
                        </div>
                      </div>
                      <div className="mb-3">
                      <button
                          onClick={handleSubmit}
                          type="submit"
                          className="btn btn-primary w-100"
                        >
                          Sign In
                        </button>
                      </div>
                      {/* <div className="text-center">
                        <h6 className="fw-normal text-dark mb-0">
                          Don’t have an account?
                          <Link to={all_routes.register} className="hover-a">
                            {" "}
                            Create Account
                          </Link>
                        </h6>
                      </div> */}
                    </div>
                    <div className="mt-5 pb-4 text-center">
                      <CopyRightInfo/>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    
    </div>

  );
};

export default Login;

